/**
=========================================================
* SuitOrbit - Rutas de Contabilidad
=========================================================

  Conjunto de rutas dedicado al módulo de Contabilidad /
  Cuentas por Pagar. Sigue el mismo esquema que `routesfac.js`:

  1. `type: "collapse"`  → entrada de menú navegable.
  2. `type: "title"`     → título dentro del Sidenav.
  3. `type: "divider"`   → divisor visual.
  4. `name`              → texto visible en el Sidenav.
  5. `key`               → identificador único (también usado
                            por `tienePermisosModulo`).
  6. `icon`              → icono mostrado en el Sidenav.
  7. `collapse`          → array con sub-rutas (menú anidado).
  8. `route`             → path de react-router para la entrada.
  9. `component`         → componente que se renderiza en la ruta.

  Para añadir un nuevo módulo de contabilidad basta con
  agregar una entrada al array `routesCont`.
*/

// Layouts
import Dashboard from "layouts/dashboard";
import Entrar from "layouts/login";

// Pantallas de Contabilidad
import CatalogoCuentasContables from "contabilidad/CatalogoCuentasContables";
import EntradasDiario from "contabilidad/EntradasDiario";
import ConfiguracionDocumentoContable from "contabilidad/ConfiguracionDocumentoContable";
import GestionFormasDePago from "contabilidad/GestionFormasDePago";
import GestionCentrosDeCosto from "contabilidad/GestionCentrosDeCosto";
import MantenimientoCuentasXPagar from "contabilidad/MantenimientoCuentasXPagar";
import AvancesSuplidor from "contabilidad/AvancesSuplidor";
import MantenimientoAvancesSuplidor from "contabilidad/MantenimientoAvancesSuplidor";
import NotaCreditoSuplidor from "contabilidad/NotaCreditoSuplidor";
import MantenimientoNotaCreditoSuplidor from "contabilidad/MantenimientoNotaCreditoSuplidor";
import AplicacionCobrosSuplidor from "contabilidad/AplicacionCobrosSuplidor";
import SoloNoFiscal from "contabilidad/SoloNoFiscal";
import PaseFiscal from "contabilidad/PaseFiscal";
import ContabilizacionDiaria from "contabilidad/ContabilizacionDiaria";
import EstadoDeResultados from "Estadosfinancieroscontables/EstadoDeResultados";
import BalanceGeneral from "Estadosfinancieroscontables/BalanceGeneral";
import EstadoDeFlujoDeEfectivo from "Estadosfinancieroscontables/EstadoDeFlujoDeEfectivo";
import BalanzaDeComprobacion from "Estadosfinancieroscontables/BalanzaDeComprobacion";
import AnexoDeclaracionFiscal from "Estadosfinancieroscontables/AnexoDeclaracionFiscal.jsx";

// Pantallas de Bancos
import ListadoConciliaciones from "Bancos/ListadoConciliaciones";

// Iconos
import Icon from "@mui/material/Icon";

const routesCont = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "collapse",
    name: "Contabilidad",
    key: "contabilidad",
    programa: "contabilidad",
    // Solo umbrella: cada hijo se muestra por su propio `key`.
    // El grupo aparece si hay al menos un hijo con permiso (SidebarFac).
    icon: <Icon fontSize="small">account_balance</Icon>,
    route: "/contabilidad/catalogodecuentas",
    component: "",
    collapse: [
      {
        type: "collapse",
        name: "Catálogo de Cuentas",
        key: "catalogodecuentas",
        route: "/contabilidad/catalogodecuentas",
        component: <CatalogoCuentasContables />,
      },
      {
        type: "collapse",
        name: "Entradas de Diario",
        key: "entradasdiario",
        route: "/contabilidad/entradasdiario",
        component: <EntradasDiario />,
      },
      {
        // Pre-vista por día de cobros y gastos (varinicial.contabilizaciondiaria);
        // con la función apagada la pantalla lo avisa.
        type: "collapse",
        name: "Contabilización Diaria",
        key: "contabilizaciondiaria",
        route: "/contabilidad/contabilizaciondiaria",
        component: <ContabilizacionDiaria />,
      },
      {
        // SidebarFac solo lo muestra en negocios con las versiones fiscal /
        // no fiscal; por URL con la función apagada, la pantalla lo avisa.
        type: "collapse",
        name: "Pase a Fiscal",
        key: "pasefiscal",
        route: "/contabilidad/pasefiscal",
        component: <PaseFiscal />,
      },
      {
        type: "collapse",
        name: "Config. Documento Contable",
        key: "configuracioncuentascontables",
        route: "/contabilidad/configuracioncuentascontables",
        component: <ConfiguracionDocumentoContable />,
      },
      {
        type: "collapse",
        name: "Formas de Pago",
        key: "gestionformasdepago",
        route: "/contabilidad/gestionformasdepago",
        component: <GestionFormasDePago />,
      },
      
      {/*
      {
        type: "collapse",
        name: "Centros de Costo",
        key: "gestioncentrosdecosto",
        route: "/contabilidad/gestioncentrosdecosto",
        component: <GestionCentrosDeCosto />,
      },
      */}

    ],
  },
  {
    type: "collapse",
    name: "Cuentas x Pagar",
    key: "cuentasxpagar",
    programa: "cuentasxpagar",
    // Solo umbrella: cada hijo se muestra por su propio `key`.
    icon: <Icon fontSize="small">payments</Icon>,
    route: "/contabilidad/mantenimientocuentasxpagar",
    component: "",
    collapse: [
      {
        type: "collapse",
        name: "Mantenimiento CxP",
        key: "mantenimientocuentasxpagar",
        route: "/contabilidad/mantenimientocuentasxpagar",
        component: <MantenimientoCuentasXPagar />,
      },
      {
        type: "hidden",
        name: "Avances a Suplidor",
        key: "avancessuplidor",
        route: "/contabilidad/avancessuplidor",
        component: (
          <SoloNoFiscal titulo="Avances a Suplidor">
            <AvancesSuplidor />
          </SoloNoFiscal>
        ),
      },
      {
        type: "collapse",
        name: "Mantenimiento Avances",
        key: "mantenimientoavancessuplidor",
        route: "/contabilidad/mantenimientoavancessuplidor",
        component: <MantenimientoAvancesSuplidor />,
      },
      {
        type: "collapse",
        name: "Nota Crédito Suplidor",
        key: "notacreditosuplidor",
        route: "/contabilidad/notacreditosuplidor",
        component: (
          <SoloNoFiscal titulo="Nota Crédito Suplidor">
            <NotaCreditoSuplidor />
          </SoloNoFiscal>
        ),
      },
      {
        type: "collapse",
        name: "Mantenimiento N/C Suplidor",
        key: "mantenimientonotascreditosuplidor",
        route: "/contabilidad/mantenimientonotascreditosuplidor",
        component: <MantenimientoNotaCreditoSuplidor />,
      },
      {
        type: "collapse",
        name: "Aplicación Cobros Suplidor",
        key: "aplicacioncobrossuplidor",
        route: "/contabilidad/aplicacioncobrossuplidor",
        component: (
          <SoloNoFiscal titulo="Aplicación Cobros Suplidor">
            <AplicacionCobrosSuplidor />
          </SoloNoFiscal>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Bancos",
    key: "bancos",
    programa: "bancos",
    // Solo umbrella: cada hijo se muestra por su propio `key`.
    // El grupo aparece si hay al menos un hijo con permiso (SidebarFac).
    icon: <Icon fontSize="small">account_balance_wallet</Icon>,
    route: "/bancos/conciliacionbancaria",
    component: "",
    collapse: [
      {
        type: "collapse",
        name: "Conciliación Bancaria",
        key: "conciliacionbancaria",
        route: "/bancos/conciliacionbancaria",
        component: <ListadoConciliaciones />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Estados Financieros",
    key: "estadosfinancieros",
    programa: "estadosfinancieros",
    // Umbrella: con permiso «estadosfinancieros» se ven todos.
    // Cada hijo también puede habilitarse por su propio key.
    icon: <Icon fontSize="small">analytics</Icon>,
    route: "/contabilidad/estadoderesultados",
    component: "",
    collapse: [
      {
        type: "collapse",
        name: "Estado de Resultados",
        key: "estadoderesultados",
        route: "/contabilidad/estadoderesultados",
        component: <EstadoDeResultados />,
      },
      {
        type: "collapse",
        name: "Balance General",
        key: "balancegeneral",
        route: "/contabilidad/balancegeneral",
        component: <BalanceGeneral />,
      },
      {
        type: "collapse",
        name: "Estado de Flujo de Efectivo",
        key: "estadoflujodeefectivo",
        route: "/contabilidad/estadoflujodeefectivo",
        component: <EstadoDeFlujoDeEfectivo />,
      },
      {
        type: "collapse",
        name: "Balanza de Comprobación",
        key: "balanzadecomprobacion",
        route: "/contabilidad/balanzadecomprobacion",
        component: <BalanzaDeComprobacion />,
      },
      {
        type: "collapse",
        name: "Anexo y Declaración",
        key: "anexodeclaracion",
        route: "/contabilidad/anexodeclaracion",
        component: <AnexoDeclaracionFiscal />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/login",
    component: <Entrar />,
  },
  {
    name: "Sign Up",
    key: "sign-up",
    route: "/login",
    component: <Entrar />,
  },
];

export default routesCont;
